import React from "react";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import styled from "styled-components";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/system";
import { ASSETS } from "../../utils/config";
import TextView from "../TextView";
import { useDispatch, useSelector } from "react-redux";
import { closeGeneralDialogModal } from "../../redux/reducers/generalDialog";
import { device } from "../../utils/device";
import useScreenSize from "../../hooks/useScreenSize";
import { Cancel } from "../../assets/icons";

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);

  @media ${device.mobilePortrait} {
    width: 90vw;
    min-height: 25vh;
    max-width: 480px;
  }

  @media (${device.mobileLandscape}) {
    width: 50vw;
    min-height: 20vh;
    max-width: 550px;
  }
`;

const StyledImage = styled.img`
  height: 8rem;
  width: 10rem;
  margin-bottom: 1rem;
  margin-top: 1rem;

  @media (min-width: 768px) {
    height: 8rem;
    width: 8rem;
  }

  @media (min-width: 1024px) {
    height: 7rem;
    width: 7rem;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
  margin-top: 20px;
  flex-direction: row;
  justify-content: space-evenly;

  @media (${device.mobilePortrait}) {
    flex-direction: column;
  }
`;

const CloseIcon = styled.div`
  position: absolute;
  z-index: 100;
  right: 24px;
  cursor: pointer;
  width: 24px;
`;

const GeneralDialog = () => {
  const { isMobileLandscape } = useScreenSize();
  const theme = useTheme();
  const dispatch = useDispatch();

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    isOpen,
    title1,
    title2,
    imageSrc,
    primaryButtonTitle,
    primaryButtonOnClick,
    secondaryButtonTitle,
    secondaryButtonOnClick,
    disableClose,
  } = useSelector((state) => state.generalDialog);

  const { isMobilePortrait } = useScreenSize();

  const handleCancel = () => {
    if (secondaryButtonOnClick) secondaryButtonOnClick();
    !disableClose && handleClose();
  };

  const handleConfirm = () => {
    if (primaryButtonOnClick) primaryButtonOnClick();
    !disableClose && handleClose();
  };

  const handleClose = () => {
    dispatch(closeGeneralDialogModal());
  };

  return (
    <StyledModal
      open={isOpen}
      onClose={disableClose ? undefined : handleClose}
      aria-labelledby="general-dialog-title"
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.5)", // Light transparent background
        },
      }}
    >
      <ModalContainer
        style={{
          position: "relative",
        }}
      >
        {!disableClose && (
          <CloseIcon>
            <Cancel onClick={handleClose} />
          </CloseIcon>
        )}
        <StyledImage src={imageSrc ?? ASSETS.DialogImg} alt="Dialog Icon" />
        {title1 && (
          <TextView variant="subtitle" style={{ fontWeight: 700 }}>
            {title1}
          </TextView>
        )}
        {title2 && (
          <TextView variant="subtitle" style={{ fontWeight: 700 }}>
            {title2}
          </TextView>
        )}

        <ButtonContainer>
          {primaryButtonTitle && (
            <Button
              variant="contained"
              onClick={handleConfirm}
              fullWidth={isMobilePortrait}
              sx={{
                backgroundColor: "#9B5AF8",
                width: fullScreen ? "100%" : "45%",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#7F47D8",
                },
                color: "#FFFFFF",
              }}
            >
              {primaryButtonTitle}
            </Button>
          )}
          {secondaryButtonTitle && (
            <Button
              variant="outlined"
              onClick={handleCancel}
              fullWidth={isMobilePortrait}
              sx={{
                borderColor: "#9B5AF8",
                width: fullScreen ? "100%" : "45%",
                color: "#9B5AF8",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#7F47D8",
                  color: "#7F47D8",
                },
              }}
            >
              {secondaryButtonTitle}
            </Button>
          )}
        </ButtonContainer>
      </ModalContainer>
    </StyledModal>
  );
};

export default GeneralDialog;
